import * as Sentry from '@sentry/browser';
import { createStore, createHook, Action } from 'react-sweet-state';

import { BuildStatusCountsMap } from 'src/types';
import { PullRequest } from 'src/types/pull-requests-table';
import { short12Hash } from 'src/utils/short-hash';

import { WorkspacePullRequestsApi } from './requests';
import { WorkspacePullRequestsState } from './types';

const addBuildStatusesToPullRequests =
  (buildStatuses: BuildStatusCountsMap): Action<WorkspacePullRequestsState> =>
  ({ getState, setState }) => {
    const { pullRequests } = getState();
    const pullRequestsWithStatus = pullRequests.map(pr => {
      const status = buildStatuses[short12Hash(pr.source.commit.hash)];
      return {
        ...pr,
        status_counts: status && status.status_counts,
      };
    });
    setState({
      pullRequests: pullRequestsWithStatus,
    });
  };

const fetchBuildStatuses =
  (
    getBuildStatuses: WorkspacePullRequestsApi['getBuildStatuses']
  ): Action<WorkspacePullRequestsState> =>
  async ({ getState, setState, dispatch }) => {
    try {
      const commitHashes = JSON.stringify({
        commits: getState().pullRequests.map(commit =>
          short12Hash(commit.source.commit.hash)
        ),
      });
      const buildStatuses = await getBuildStatuses(commitHashes);
      dispatch(addBuildStatusesToPullRequests(buildStatuses));
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setState({
        buildStatusesFetched: true,
      });
    }
  };

export const setPullRequests =
  (
    { getBuildStatuses }: WorkspacePullRequestsApi,
    pullRequests: PullRequest[]
  ): Action<WorkspacePullRequestsState> =>
  ({ setState, dispatch }) => {
    setState({ pullRequests });
    dispatch(fetchBuildStatuses(getBuildStatuses));
  };

export const actions = {
  addBuildStatusesToPullRequests,
  fetchBuildStatuses,
  setPullRequests,
};

const initialState: WorkspacePullRequestsState = {
  pullRequests: [],
  buildStatusesFetched: false,
};

const WorkspacePullRequestsStore = createStore({
  initialState,
  actions,
});

export const useWorkspacePullRequests = createHook(WorkspacePullRequestsStore);
